/**
 * Phase 11 — thin wrapper that wires the platform-ops handler registry
 * + the `session.tool` listener once auth + gateway are ready.
 *
 * Mounted in `App.tsx` above route children. The provider itself
 * renders only its children — its job is the side effect of
 * subscribing to the event bus + registering handlers at the right
 * lifecycle point.
 *
 * **Thin-slice X.1.3 catalog source.** The SPA bundles a copy of the
 * catalog via the `@lexiun/lx-platform-ops` workspace dep (X.1.1).
 * The capabilities-publish handshake + intersection-mode handling
 * (Layer 2 of drift prevention) lands in X.2.
 */
import { useEffect, type ReactNode } from "react";
import { CatalogSchema, type Catalog } from "@lexiun/lx-platform-ops";
import platformOpsCatalog from "@lexiun/lx-platform-ops/catalog.json";
import { registerPlatformOpsHandlers } from "@/lib/platform-ops-handlers";
import { usePlatformOpListener } from "@/hooks/usePlatformOpListener";
import { ToastOverlay } from "@/components/workspace/ToastOverlay";
import { CopyMoveDropOverlay } from "@/components/workspace/CopyMoveDropOverlay";
import { OpBlockOverlay } from "@/components/workspace/OpBlockOverlay";

interface PlatformOpsProviderProps {
  children: ReactNode;
}

// Validate the bundled catalog once at module init — if the embedded
// catalog is malformed something is very wrong with the build.
const VALIDATED_CATALOG: Catalog = CatalogSchema.parse(platformOpsCatalog);

export function PlatformOpsProvider({ children }: PlatformOpsProviderProps) {
  // Bootstrap handlers once (idempotent — internal flag in the bootstrap).
  useEffect(() => {
    registerPlatformOpsHandlers();
  }, []);

  // Always-on subscriber. EventFrames dispatched while disconnected are
  // simply not received (per `useEventStream` design); no
  // connection-state gating needed here.
  usePlatformOpListener({ catalogOps: VALIDATED_CATALOG.operations });

  // (lx_todo's `useReasoningListener` was retired by the chat-content-
  // model redesign — tool_use blocks now live inline in
  // ChatMessage.content + render via <BlockRenderer> → <ToolUseBlock>.
  // See ai-docs/lexiun-chat-content-model-plan.md §2.)

  // ToastOverlay is rendered here so platform-ops `ide.toast.show`
  // calls produce a visible toast from any route — Home, Research,
  // Case workspace, etc. The Phase 5.5.b sibling in CaseWorkspaceLayout
  // was removed in X.1.3 so the same toast doesn't render twice on
  // case-workspace routes.
  return (
    <>
      {children}
      <ToastOverlay />
      {/* CCM copy/move drag-to-destination overlay (§1.1) — app-global,
          invisible until a `sources/` resource drag begins. */}
      <CopyMoveDropOverlay />
      {/* IDE-wide blocking spinner while a copy/move op is in flight
          (operator brief 2026-07-03) — invisible until an op begins. */}
      <OpBlockOverlay />
    </>
  );
}
